import Vue from 'vue';
import moment from 'moment';
import numeral from 'numeral';
import { diff, addedDiff, deletedDiff, updatedDiff, detailedDiff } from 'deep-object-diff';
import StringMask from 'string-mask';

/* String Extensions *******************************************************************/

if (!String.prototype.reverse) {
   String.prototype.reverse = function () {
      return this.split('').reverse().join('');
   };
}

if (!String.prototype.format) {
   String.prototype.format = function () {
      let args = arguments;
      return this.replace(/{(\d+)}/g, function (match, number) {
         return typeof args[number] != 'undefined'
            ? args[number]
            : match;
      });
   };
}

if (!String.prototype.padRight) {
   String.prototype.padRight = function (padSize, padString, truncate) {
      let s = S(this).padRight(padSize, padString);
      if (truncate) return s.left(padSize).s;
      return s.s;
   };
}

if (!String.prototype.padLeft) {
   String.prototype.padLeft = function (padSize, padString, truncate) {
      let s = S(this).padLeft(padSize, padString);
      if (truncate) return s.right(padSize).s;
      return s.s;
   };
}

if (!String.prototype.toCapitalize) {
   String.prototype.toCapitalize = function () {
      return this.toLowerCase().replace(/(^|\s)\S/g, function (l) {
         return l.toUpperCase();
      });
   };
}

if (!String.prototype.unMaskedNumber) {
   String.prototype.unMaskedNumber = function () {
      return numeral(this).value();
   };
}


/***********************************************************************************/


const Mask = {};
Mask.rs = {
   decimal: ',',
   thousands: '.',
   prefix: 'R$ ',
   suffix: '',
   precision: 2,
   masked: false
};
Mask.fixed_rs = {
   decimal: ',',
   thousands: '.',
   prefix: '',
   suffix: '',
   precision: 2,
   masked: false
};
Mask.us = {
   decimal: '.',
   thousands: ',',
   prefix: '$ ',
   suffix: '',
   precision: 2,
   masked: true
};
Mask.fixed_us = {
   decimal: '.',
   thousands: ',',
   prefix: '',
   suffix: '',
   precision: 2,
   masked: true
};
Mask.ssn = '###-##-###';
Mask.cpf = '###.###.###-##';
Mask.brPhone = ['(##) ####-####', '(##) # ####-####'];
Mask.usPhone = '(###) ###-####';
Mask.state = 'AA';
Mask.zipcode = '#####';


const dateRange = {
   today: {
      start: (fmt) => moment().format(fmt),
      end: (fmt) => moment().format(fmt),
      compareTo: 'yesterday'
   },
   yesterday: {
      start: (fmt) => moment().add(-1, 'd').format(fmt),
      end: (fmt) => moment().add(-1, 'd').format(fmt),
   },
   tomorrow: {
      start: (fmt) => moment().add(1, 'd').format(fmt),
      end: (fmt) => moment().add(1, 'd').format(fmt),
   },
   thisWeek: {
      start: (fmt) => moment().startOf('week').format(fmt),
      end: (fmt) => moment().format(fmt),
      compareTo: 'lastWeek'
   },
   lastWeek: {
      start: (fmt) => moment(moment().add(-1, 'w')).startOf('week').format(fmt),
      end: (fmt) => moment().startOf('week').add(-1, 'd').format(fmt),
      compareTo: 'twoWeeksAgo'
   },
   nextWeek: {
      start: (fmt) => moment(moment().add(1, 'w')).startOf('week').format(fmt),
      end: (fmt) => moment(moment().add(1, 'w')).endOf('week').format(fmt)
   },
   twoWeeksAgo: {
      start: (fmt) => moment(moment().add(-2, 'w')).startOf('week').format(fmt),
      end: (fmt) => moment(moment().add(-1, 'w')).startOf('week').add(-1, 'd').format(fmt)
   },
   thisMonth: {
      start: (fmt) => moment().startOf('month').format(fmt),
      end: (fmt) => moment().format(fmt),
      compareTo: 'lastMonth'
   },
   lastMonth: {
      start: (fmt) => moment(moment().add(-1, 'M')).startOf('month').format(fmt),
      end: (fmt) => moment(moment().add(-1, 'M')).endOf('month').format(fmt),
      compareTo: 'twoMonthsAgo'
   },
   twoMonthsAgo: {
      start: (fmt) => moment(moment().add(-2, 'M')).startOf('month').format(fmt),
      end: (fmt) => moment(moment().add(-2, 'M')).endOf('month').format(fmt)
   },
   nextMonth: {
      start: (fmt) => moment(moment().add(1, 'M')).startOf('month').format(fmt),
      end: (fmt) => moment(moment().add(1, 'M')).endOf('month').format(fmt)
   },
   last3Months: {
      start: (fmt) => moment(moment().add(-3, 'M')).startOf('month').format(fmt),
      end: (fmt) => moment(moment().add(-1, 'M')).endOf('month').format(fmt),
      compareTo: 'twoQuartersAgo'
   },
   twoQuartersAgo: {
      start: (fmt) => moment(moment().add(-6, 'M')).startOf('month').format(fmt),
      end: (fmt) => moment(moment().add(-4, 'M')).endOf('month').format(fmt)
   },
   thisYear: {
      start: (fmt) => moment().startOf('year').format(fmt),
      end: (fmt) => moment().format(fmt),
      compareTo: 'lastYear'
   },
   lastYear: {
      start: (fmt) => moment(moment().add(-1, 'y')).startOf('year').format(fmt),
      end: (fmt) => moment(moment().add(-1, 'y')).endOf('year').format(fmt)
   }
};

const vtfRules = {
   required: (v) => !!v || 'Faltou esta informação',
   email: (value) => {
      const pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return (value) ? (pattern.test(value) || 'Email inválido') : (!value);
   },
   gtZero: (v) => numeral(v).value() > 0 || 'Informar valor'
};


const rules = function (options) {
   let rls = [];
   for (let i = 0; i < options.length; i++) {
      rls.push(vtfRules[options[i]]);
   }
   return rls;
};

const unformat = function (input, precision) {
   if (typeof input === 'number' || input === null || input === '') {
      return input;
   }

   function onlyNumbers (input) {
      return toStr(input).replace(/\D+/g, '') || '0';
   }

   function numbersToCurrency (numbers, precision) {
      var exp = Math.pow(10, precision);
      var float = parseFloat(numbers) / exp;
      return float.toFixed(fixed(precision));
   }

   function toStr (value) {
      return value ? value.toString() : '';
   }

   function fixed (precision) {
      return between(0, precision, 20);
   }

   function between (min, n, max) {
      return Math.max(min, Math.min(n, max));
   }

   input = input.toString();

   var negative = input.indexOf('-') === 0 ? -1 : 1;
   var numbers = onlyNumbers(input);
   var currency = numbersToCurrency(numbers, precision);
   return parseFloat(currency) * negative;
};

const sameObject = function (obj1, obj2) {
   let str1,
      str2;
   try {
      str1 = JSON.stringify(obj1);
   } catch (e) {
      str1 = '';
   }
   try {
      str2 = JSON.stringify(obj2);
   } catch (e) {
      str2 = '';
   }

   // console.log('str1: ', str1)
   // console.log('str2: ', str2)
   return str1 === str2;

};

const getObject = function (anArray, rules) {
   let data = [];
   let keys = Object.keys(rules || {});

   if (anArray && anArray.length) {
      data = anArray.filter(v => {
         let match = true;
         keys.forEach(k => match = match && v[k] == rules[k]);
         return match;
      });
   }

   return data;

};

const objectExtract = function (anArray, objKeys, except) {
   let data = [];
   let exceptKeys = Object.keys(except || {});

   if (anArray && anArray.length) {
      anArray.forEach((v) => {
         let skip = false;
         exceptKeys.forEach((e) => {
            if (!skip) skip = (v[e] == except[e]);
         });
         if (!skip) {
            let obj = {};
            objKeys.forEach((k) => {
               obj[k] = v[k];
            });
            data.push(obj);
         }
      });
   }

   return data;

};

const updateObj = function (target, source) {
   Object.keys(target).forEach((key) => {
      // console.log(key);
      if (source.hasOwnProperty(key)) {
         target[key] = source[key];
      }
   });
};

const numericArraySort = function (anArray) {
   function compararNumeros (a, b) {
      return a - b;
   }

   return anArray.sort(compararNumeros);

};


const randomElement = (arr = []) => {
   return arr[Math.floor(Math.random() * arr.length)];
};

const kebab = (str) => {
   return (str || '').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

const toggleFullScreen = () => {
   let doc = window.document;
   let docEl = doc.documentElement;

   let requestFullScreen = docEl.requestFullscreen || docEl.mozRequestFullScreen || docEl.webkitRequestFullScreen || docEl.msRequestFullscreen;
   let cancelFullScreen = doc.exitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen || doc.msExitFullscreen;

   if (!doc.fullscreenElement && !doc.mozFullScreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      requestFullScreen.call(docEl);
   } else {
      cancelFullScreen.call(doc);
   }
};

const setTempValue = (aVar, aValue, mSec) => {
   let oldValue = aVar;
   setTimeout(() => aVar = oldValue, mSec);
   aVar = aValue;
};

const objectCopy = function (obj) {
   return JSON.parse(JSON.stringify(obj));
};

const objectDiff = function (original, updated) {
   return diff(original, updated);
};

const initials = function (name) {
   let w = name.split(' ');
   if (w.length > 1) {
      return ''.concat(w[0][0], w[w.length - 1][0]).toUpperCase();
   } else {
      return name.substr(0, 2).toUpperCase();
   }
};

const wait = (ms) => {
   return new Promise(resolve => {
      setTimeout(() => resolve(), ms || 300);
   });
};

const applyMask = (data, mask) => {
   return StringMask.apply(data, mask);
};

const util = {
   applyMask,
   Mask,
   dateRange,
   randomElement,
   toggleFullScreen,
   kebab,
   rules,
   unformat,
   sameObject,
   getObject,
   objectExtract,
   updateObj,
   numericArraySort,
   setTempValue,
   objectCopy,
   objectDiff,
   diff,
   updatedDiff,
   initials,
   wait
};


Vue.prototype.$util = util;

Vue.prototype.$uCan = function (params) {
   let donts = window.localStorage.getItem('stDonts');

   try {
      donts = JSON.parse(donts);
   } catch (e) {
      console.error('User role DONTs JSON is malformed!');
      return true;
   }

   if (params.key) {
      return donts && donts.hasOwnProperty(params.key) ? donts[params.key] : '';
   }

   if (donts) {
      // console.log('donts');
      if (params.rid) {
         if (donts.pages) {
            let page = params.rid.split('.')[0];
            let field = params.rid.split('.')[1];

            if (donts.pages[page]) {
               for (let i = 0; i < donts.pages[page].length; i++) {
                  if (donts.pages[page][i].split('|')[0] === field) {
                     return (donts.pages[page][i].split('|')[1].indexOf(params.op) === -1);
                  }
               }
            }
         }
      } else if (params.route && donts.routes) {
         return (donts.routes.indexOf(params.route) === -1);
      } else if (params.op && donts.operations) {
         return (donts.operations.indexOf(params.op) === -1);
      }
   }

   return true;

};

export default util;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@consigmais-team/vue-common/util/index.js
// module id = null
// module chunks = 